import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { PROJECTS, CATEGORIES } from "@/data/site";
import SEO from "@/components/SEO";

export default function Portfolio() {
  const [active, setActive] = useState("All");

  const filtered = useMemo(
    () => (active === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === active)),
    [active]
  );

  const counts = useMemo(() => {
    const m = { All: PROJECTS.length };
    PROJECTS.forEach((p) => { m[p.category] = (m[p.category] || 0) + 1; });
    return m;
  }, []);

  const portfolioSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "AmagicTech Solutions Portfolio & Case Studies",
    "url": "https://amagictechsolutions.com/portfolio",
    "description": "Explore our portfolio of delivered web platforms, mobile apps, and enterprise solutions.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": PROJECTS.map((p, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "item": {
          "@type": "CreativeWork",
          "name": p.title,
          "description": p.description,
          "url": p.link && p.link !== "#" ? p.link : "https://amagictechsolutions.com/portfolio",
          "image": p.image && !p.image.startsWith("http") ? `https://amagictechsolutions.com${p.image}` : p.image
        }
      }))
    }
  };

  return (
    <main data-testid="portfolio-page" className="pt-20">
      <SEO 
        title="Our Portfolio & Work"
        description="Explore 28+ shipped web applications, mobile apps, SaaS platforms, and enterprise solutions built by AmagicTech Solutions."
        url="/portfolio"
        keywords="portfolio, case studies, web development projects, mobile apps, SaaS platforms, react apps, AmagicTech portfolio"
        schema={portfolioSchema}
      />
      <section className="container-tight">
        <Reveal>
          <SectionLabel testid="portfolio-label">Portfolio</SectionLabel>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-zinc-900 leading-[0.98]">
            Selected work.
            <br /><span className="text-zinc-500">28+ projects shipped.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl text-zinc-600 text-lg leading-relaxed">
            Across e-commerce, manufacturing, construction, fintech and fashion —
            here&apos;s a curated slice of what we&apos;ve built for clients in India, Oman, and beyond.
          </p>
        </Reveal>
      </section>

      <section className="container-tight mt-16">
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 pb-4">
          {CATEGORIES.map((c) => {
            const isActive = c === active;
            return (
              <button
                key={c}
                type="button"
                data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm border transition-colors ${
                  isActive
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-transparent text-zinc-600 border-zinc-300 hover:text-zinc-900 hover:border-zinc-600"
                }`}
              >
                {c}
                <span className={`font-mono-tech text-[10px] ${isActive ? "text-zinc-400" : "text-zinc-400"}`}>
                  {counts[c] || 0}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24">
          {filtered.map((p, idx) => (
            <Reveal key={`${active}-${p.title}`} delay={Math.min(idx * 0.04, 0.4)}>
              <a
                href={p.link || "#"}
                target={p.link && p.link !== "#" ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={`portfolio-card-${idx}`}
                className="group block border border-zinc-200 hover:border-zinc-400 hover:shadow-lg hover:shadow-zinc-100/50 transition-all duration-300 h-full overflow-hidden bg-[#FAFAF7]/50 hover:bg-white flex flex-col"
              >
                {p.image && (
                  <div className="aspect-[16/10] overflow-hidden bg-zinc-100 border-b border-zinc-200 relative">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6 md:p-8 flex flex-col flex-grow justify-between">
                  <div>
                    <div className="flex items-center justify-between border-b border-zinc-100 pb-4">
                      <div className="bg-zinc-900 text-white px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.15em] uppercase">
                        {p.category}
                      </div>
                      <p className="text-zinc-400 text-xs font-mono-tech">{p.client}</p>
                    </div>
                    <div className="mt-6">
                      <h3 className="text-zinc-900 font-display text-xl tracking-tight flex items-start justify-between gap-3">
                        <span>{p.title}</span>
                        <ArrowUpRight className="w-4 h-4 text-zinc-400 group-hover:text-zinc-900 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all mt-1 shrink-0" />
                      </h3>
                      <p className="mt-3 text-zinc-600 text-[14px] leading-relaxed line-clamp-3">{p.description}</p>
                    </div>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
